<template>
  <div class="setConfigurator">
    <v-stepper v-model="step" vertical :non-linear="true" class="configurationStepper">
      <v-stepper-step v-if="personSelectionNeeded"
                      :complete="step > personSelectionStep"
                      :step="personSelectionStep"
                      edit-icon="check"
                      editable
      >{{ $t("CONFIGURATOR_CHOOSE_PERSON") }}
        <small>{{ $t("CONFIGURATOR_CHOOSE_PERSON_HINT") }}</small>
      </v-stepper-step>
      <v-stepper-content v-if="personSelectionNeeded" :step="personSelectionStep">
        <PersonSelector :value="personIndex" @input="selectPerson" :persons="eligiblePersons" @select="onPersonSelect"/>
      </v-stepper-content>

      <v-stepper-step v-if="configurationTypeSelectionNeeded"
                      :complete="step > configurationTypeSelectionStep"
                      :step="configurationTypeSelectionStep"
                      edit-icon="check"
                      editable
      >{{ $t("CONFIGURATOR_CHOOSE_MODE") }}
      </v-stepper-step>
      <v-stepper-content v-if="needsToChooseDetailedConfiguration" :step="configurationTypeSelectionStep">
        <ConfigurationModeSelector :value="configurationMode" @input="selectMode"
                                   :product="product"
                                   ref="modeSelector"
                                   @select="nextStep"/>
      </v-stepper-content>

      <v-stepper-step
          :complete="step > imageSelectionStep"
          :step="imageSelectionStep"
          edit-icon="check"
      >{{ $t("CONFIGURATOR_CHOOSE_IMAGES") }}
      </v-stepper-step>
      <v-stepper-content :step="imageSelectionStep" class="imageStepperContent">
        <MultipleRequirementsSelector v-if="step === imageSelectionStep && (!prefill || priorStepsChosen && person)"
                                      v-model="imageSelection"
                                      class="multipleRequirementImageSelector"
                                      :requirements="imageRequirements"
                                      :person="person"
                                      :detailed-configuration="isDetailedConfiguration"
                                      :preserve-prefilled-choices="!!prefill"
                                      ref="imageSelector"/>
      </v-stepper-content>
    </v-stepper>
    <v-row class="actionButtons" justify="center">
      <v-col cols="auto">
        <v-btn v-if="configurationComplete" color="primary" class="actionButton"  :loading="adding" :disabled="adding" @click="addToBasket">
          <v-icon>{{ basketProductSubmission && basketProductSubmission.editing ? "save" : "add_shopping_cart" }}</v-icon>
          {{ $t(basketProductSubmission && basketProductSubmission.editing ? "BASKET_SAVE_CONFIGURATION" : "BUTTON_ADD_TO_BASKET") }}
        </v-btn>
        <v-btn v-else-if="mayShowContinueButton" color="primary" class="actionButton" @click="continueImageSelection">
          {{ $t("BUTTON_CONTINUE") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {
  ConfiguredImageDto,
  DetailedImageRequirementDto,
  ImageRequirementDto,
  PersonDto,
  ProductDto,
  ProductMediumDto,
  ProductTypeDto
} from "@swagger";
import {Prop, Watch} from "vue-property-decorator";
import ImageSelector from "@/components/product-configuration/ImageSelector.vue";
import PersonSelector from "@/components/product-configuration/PersonSelector.vue";
import ConfigurationModeSelector from "@/components/product-configuration/ConfigurationModeSelector.vue";
import MultipleRequirementsSelector from "@/components/product-configuration/MultipleRequirementsSelector.vue";
import {ConfigurationChoices, mapConfigurationPrefill, ProductConfigurationPrefill} from '@/global/ProductConfigurationPrefill';

@Component({
  components: {MultipleRequirementsSelector, ConfigurationModeSelector, PersonSelector, ImageSelector}
})
export default class SetConfigurator extends BasicComponent {

  @Prop(Object) product: ProductDto;
  @Prop(Object) prefill: ProductConfigurationPrefill;

  step: number = 1;
  personIndex: number | null = null;
  configurationMode: 'AUTO' | 'DETAILED' | null = null;
  explicitPersonCode: string | null = null;
  selectingImages: boolean = false;
  autoSelection: ConfiguredImageDto[][] = []; // The imageSelection selected in automatic mode
  detailedSelection: ConfiguredImageDto[][] = []; // The imageSelection selected in detailed mode

  created() {
    this.resetImageSelection();
    if (!this.prefill) {
      return;
    }
    const code = this.prefill?.choices?.personCode;
    const index = this.eligiblePersons.findIndex(person => person.code === code);
    if (index >= 0) {
      this.personIndex = index;
      this.explicitPersonCode = code;
    }
    this.configurationMode = this.prefill?.choices?.mode ?? null;
    if (this.prefill && this.person) this.seedPrefill();
    this.step = !this.personChosen ? this.personSelectionStep
      : !this.modeChosen ? this.configurationTypeSelectionStep : this.imageSelectionStep;
  }

  selectPerson(index: number) {
    this.personIndex = index;
    this.explicitPersonCode = this.eligiblePersons[index]?.code ?? null;
  }

  onPersonSelect() {
    this.step = this.modeChosen ? this.imageSelectionStep : this.configurationTypeSelectionStep;
  }

  selectMode(mode: 'AUTO' | 'DETAILED') {
    this.configurationMode = mode;
  }

  get personChosen(): boolean {
    return !!this.person;
  }

  get modeChosen(): boolean {
    return !this.configurationTypeSelectionNeeded || this.configurationMode !== null;
  }

  get priorStepsChosen(): boolean {
    return this.personChosen && this.modeChosen;
  }

  get choices(): ConfigurationChoices {
    return {personCode: this.explicitPersonCode ?? undefined, mode: this.configurationMode ?? undefined};
  }

  seedPrefill() {
    const images = this.getPersonImages(this.person);
    this.autoSelection = mapConfigurationPrefill(this.prefill, this.autoImageRequirements, images, false);
    this.detailedSelection = mapConfigurationPrefill(this.prefill, this.product.detailedImageRequirements, images, true);
  }

  mounted() {
    if (this.step === this.imageSelectionStep) {
      this.selectingImages = true;
    }
  }

  get imageSelection(): ConfiguredImageDto[][] {
    return this.isDetailedConfiguration ? this.detailedSelection : this.autoSelection;
  }

  set imageSelection(value: ConfiguredImageDto[][]) {
    if (this.isDetailedConfiguration) {
      this.detailedSelection = value;
    }
    else this.autoSelection = value;
  }

  get eligiblePersons(): PersonDto[] {
    return this.allPersons.filter((person: PersonDto) => {
      if (this.prefill && !this.prefill.personCodes.includes(person.code)) {
        return false;
      }
      const images = this.getPersonImages(person);
      return person.productIds.includes(this.product.id)
          && this.imageRequirements.every(r => images.some(image => this.imageMatchesRequirement(image, r.requirement)));
    });
  }

  nextStep() {
    if (!this.prefill || this.priorStepsChosen) this.step = this.imageSelectionStep;
  }

  /**
   * @return A human-readable description of the requirement
   */
  getAutoRequirementLabel(requirement: ImageRequirementDto): string {
    if (requirement.type === ImageRequirementDto.TypeEnum.ALLOFPERSON) return this.product.detailedImageRequirements.find(r => r.requirement.type === requirement.type)?.productName || this.product.name;
    return requirement.types.map(t => this.$t("IMAGE_NAME_" + t)).join(" / ");
  }

  /**
   * Tells the image selector to go to the next step
   */
  continueImageSelection(): void {
    this.getImageSelector().navigateToNextStep();
  }

  adding = false;

  async addToBasket(): Promise<void> {
    if (this.adding || !this.configurationComplete) {
      return;
    }
    this.adding = true;
    await this.addConfiguredProducts([{productId: this.product.id, images: this.flattenArray(this.imageSelection),
      detailedConfiguration: this.isDetailedConfiguration}], items => {
      this.$emit("addedToBasket", items);
    }, (error) => {
      this.$emit("error", error);
    }, [this.choices]);
    this.adding = false;
  }

  get imageRequirements(): DetailedImageRequirementDto[] {
    if (this.isDetailedConfiguration) {
      return this.product.detailedImageRequirements;
    }
    return this.autoImageRequirements;
  }

  get autoImageRequirements(): DetailedImageRequirementDto[] {
    const shared = this.product.imageRequirements.filter(r => r.type !== ImageRequirementDto.TypeEnum.ALLOFPERSON);
    const requirements = shared.length ? shared : this.product.imageRequirements;
    const automaticDownloads = shared.length ? this.product.detailedImageRequirements.filter(
        r => r.requirement.type === ImageRequirementDto.TypeEnum.ALLOFPERSON) : [];
    return requirements.map(r => {
      return {
        requirement: r,
        productName: this.getAutoRequirementLabel(r)
      };
    }).concat(automaticDownloads);
  }

  get isDetailedConfiguration() {
    if (!this.autoConfigurationPossible) {
      return true;
    }
    if (this.basketProductSubmission?.editing && this.configurationMode === "DETAILED") {
      return true;
    }
    if (this.detailedConfigurationObsolete) {
      return false;
    }
    return this.configurationMode === "DETAILED";
  }

  get detailedConfigurationObsolete() {
    const requirements = this.product.imageRequirements;
    if (requirements.some(r => r.type === ImageRequirementDto.TypeEnum.ALLOFPERSON)
        && requirements.some(r => r.type === ImageRequirementDto.TypeEnum.SINGLE)) return false;
    return this.product.imageRequirements.length === this.product.detailedImageRequirements.length;
  }

  get autoConfigurationPossible() {
    const detailedRequirements = this.product.detailedImageRequirements;
    if (detailedRequirements.filter(r => r.productMedium === ProductMediumDto.DOWNLOAD
        && r.requirement.type === ImageRequirementDto.TypeEnum.SINGLE).length > 1) {
      return false;
    }
    return true;
  }

  get needsToChooseDetailedConfiguration() {
    if (this.product.productType !== ProductTypeDto.SET) {
      return false;
    }
    if (this.detailedConfigurationObsolete || !this.autoConfigurationPossible) {
      return false;
    }
    return true;
  }

  get personSelectionNeeded() {
    const persons = this.eligiblePersons;
    // TODO Automatic person selection for friend image products
    return persons.length > 1;
  }

  get personSelectionStep() {
    return this.personSelectionNeeded ? 1 : 0;
  }

  get configurationTypeSelectionNeeded() {
    return this.needsToChooseDetailedConfiguration;
  }

  get configurationTypeSelectionStep() {
    return this.personSelectionStep + (this.configurationTypeSelectionNeeded ? 1 : 0);
  }

  get imageSelectionStep() {
    return this.configurationTypeSelectionStep + 1;
  }

  get person(): PersonDto {
    if (!this.prefill) {
      return this.eligiblePersons[this.personIndex ?? 0];
    }
    return this.explicitPersonCode ? this.eligiblePersons.find(person => person.code === this.explicitPersonCode)
      : this.eligiblePersons.length === 1 ? this.eligiblePersons[0] : undefined;
  }

  getImageSelector(): MultipleRequirementsSelector {
    return this.$refs["imageSelector"] as MultipleRequirementsSelector;
  }

  get configurationComplete(): boolean {
    return (!this.prefill || this.priorStepsChosen && !!this.person)
      && this.selectingImages && !!this.getImageSelector()?.configurationComplete;
  }

  get mayShowContinueButton(): boolean {
    return this.selectingImages && !!this.getImageSelector()?.currentRequirementSatisfied;
  }

  @Watch("product")
  onProductChange(newProduct: ProductDto, oldProduct: ProductDto): void {
    this.resetImageSelection();
  }

  @Watch("personIndex")
  onPersonChange(newIndex: number, oldIndex: number): void {
    this.resetImageSelection();
    if (this.prefill && this.person) this.seedPrefill();
  }

  resetImageSelection(): void {
    this.autoSelection = this.autoImageRequirements.map(_ => []);
    this.detailedSelection = this.product.detailedImageRequirements.map(_ => []);
  }

  @Watch("step")
  onStepChange(step, oldVal) {
    if (step === this.imageSelectionStep) {
      this.$nextTick(() => this.selectingImages = true);
    } else {
      this.selectingImages = false;
    }
    if (this.prefill) {
      if (!this.personChosen && step !== this.personSelectionStep) this.step = this.personSelectionStep;
      else if (!this.modeChosen && step === this.imageSelectionStep) this.step = this.configurationTypeSelectionStep;
    }
  }

}

</script>
<style lang="scss" scoped>
  .setConfigurator {
    max-height: 100%;
    display: flex;

    .configurationStepper {
      max-height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .imageStepperContent {
        display: flex;
        max-height: 100%;
        flex-direction: column;
        flex: 0 1 auto;
        overflow-x: hidden;
        overflow-y: scroll;
      }
    }
  }
</style>
<style lang="scss">
.configurationStepper {
  .v-stepper__content {
    &:last-of-type {
      margin-left: 8px;
    }
  }
}
</style>
